import { Fragment, memo, useEffect, useMemo, useRef, useState } from "react";
import type { TFunction } from "i18next";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import {
  FilePlusIcon,
  FileTextIcon,
  GlobeHemisphereWestIcon,
  RocketLaunchIcon,
  WarningIcon,
  XIcon
} from "@phosphor-icons/react";
import {
  Attachment,
  AttachmentAction,
  AttachmentActions,
  AttachmentContent,
  AttachmentDescription,
  AttachmentMedia,
  AttachmentTitle
} from "@libs/zapi-ui/ds/components/attachment";
import { Bubble, BubbleContent } from "@libs/zapi-ui/ds/components/bubble";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Message, MessageAvatar, MessageContent } from "@libs/zapi-ui/ds/components/message";
import {
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerProvider,
  MessageScrollerViewport
} from "@libs/zapi-ui/ds/components/message-scroller";
import { Replies, Reply } from "@libs/zapi-ui/ds/components/reply";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import {
  LeoAvatar,
  LeoThinking,
  LeoUserBubble,
  LeoWelcome,
  LeoWelcomePill
} from "@/zapi-frontend/widgets/CreateProjectChat/ui/blocks/LeoChat";
import { displayLink } from "@/zapi-frontend/shared/lib/link";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { ChatMessage as ChatMessageType } from "@/zapi-frontend/stores/chatStore";
import { parseMessageContent } from "@/zapi-frontend/shared/ui/custom/ChatMessage/lib/parseMessageContent";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { removeTimestampPrefix } from "@/zapi-frontend/shared/lib/splitFileName";
import {
  boldQuestionLabel,
  parseDimensionAnswerMessage,
  parseNavMarkerMessage,
  parseVaultItemsMessage,
  type TDocumentReadStatus
} from "./CaseBriefChatMessage";
import type { CreateProjectChatMessageListProps } from "./CreateProjectChatMessageList";

// Walkthrough messages the user can answer with "Yes, that's right" or their own words.
const CONFIRMABLE_WALKTHROUGH_KEYS = new Set([
  "walkthroughFormat",
  "walkthroughScope",
  "walkthroughIntent",
  "walkthroughCheck",
  "walkthroughReady"
]);
const WALKTHROUGH_CLOSING_KEYS = new Set(["walkthroughDone", "walkthroughDoneWithGaps"]);
// Leo's messages that ask the user something; they show in the design's question block.
const QUESTION_KEYS = new Set([
  "decisionQuestion",
  "decisionFromDocuments",
  "walkthroughQuestion",
  ...CONFIRMABLE_WALKTHROUGH_KEYS
]);
const QUESTION_LABEL = /(?:^|\n)[ \t]*\*{0,2}Question:\*{0,2}[ \t]*/;
const ATTACHMENTS_SHOWN = 3;

// A value a template sets in bold reads in the design's regular weight, in quotes.
const quoteValues = (text: string) => text.replace(/\*\*(.+?)\*\*/g, "“$1”");

// The lead-in and the question of one of Leo's turns: a template question is all question; Sherpa's own replies
// put the question after a "Question:" line.
function splitQuestion(content: string, key: string | null): { lead: string; question: string | null } {
  if (key && QUESTION_KEYS.has(key)) return { lead: "", question: quoteValues(content) };
  const match = content.match(QUESTION_LABEL);
  if (!match || match.index === undefined) return { lead: content, question: null };
  const question = content.slice(match.index + match[0].length).trim();
  return question ? { lead: content.slice(0, match.index).trim(), question } : { lead: content, question: null };
}

const parsedJson = (message: ChatMessageType) => {
  try {
    return JSON.parse(message.content);
  } catch {
    return null;
  }
};
const messageType = (message: ChatMessageType): string | null =>
  message.role === "assistant" ? parsedJson(message)?.type ?? null : null;
const templateKey = (message: ChatMessageType): string | null => parsedJson(message)?.templateKey ?? null;
const templateOptions = (message: ChatMessageType): string[] | null => {
  const options = parsedJson(message)?.templateParams?.options;
  return Array.isArray(options) ? options.filter((o): o is string => typeof o === "string") : null;
};

const isWebUrl = (value: string) => /^https?:\/\//i.test(value);

// How Leo's last message is answered: with its quick replies (typing works too), or only by typing.
export function answerMode(message: ChatMessageType | undefined, step: string): "choices" | "typed" | null {
  if (!message || message.role !== "assistant") return null;
  const key = templateKey(message);
  if (step === "decision") {
    if (key === "decisionFromDocuments") return "choices";
    return key === "decisionQuestion" ? "typed" : null;
  }
  if (step !== "chat") return null;
  if (key && CONFIRMABLE_WALKTHROUGH_KEYS.has(key)) return "choices";
  if (key === "walkthroughQuestion") return templateOptions(message)?.length ? "choices" : "typed";
  return null;
}

function LeoMarkdown({ content }: { content: string }) {
  return (
    <ReactMarkdown
      rehypePlugins={[rehypeRaw]}
      components={{
        a: ({ children, ...props }) => {
          const isInternal =
            props.href?.startsWith("/") ||
            props.href?.includes("platform.innovera") ||
            props.href?.includes("localhost");
          return (
            <a
              {...props}
              {...(isInternal ? {} : { target: "_blank", rel: "noopener noreferrer" })}
              className="text-primary-text underline underline-offset-2"
            >
              {children}
            </a>
          );
        },
        p: (props) => <p {...props} className="mb-5 last:mb-0" />,
        ul: (props) => <ul {...props} className="mb-5 list-disc space-y-1 ps-5 last:mb-0" />,
        ol: (props) => <ol {...props} className="mb-5 list-decimal space-y-1 ps-5 last:mb-0" />,
        strong: (props) => <strong {...props} className="font-semibold" />
      }}
    >
      {boldQuestionLabel(content)}
    </ReactMarkdown>
  );
}

function DocumentStatusAttachment({
  label,
  isLink,
  status,
  onRemove,
  removeLabel,
  t
}: {
  label: string;
  isLink: boolean;
  status?: TDocumentReadStatus;
  onRemove?: () => void;
  removeLabel?: string;
  t: TFunction<"createProject">;
}) {
  const Icon = isLink ? GlobeHemisphereWestIcon : FileTextIcon;
  const state = status === "reading" ? "processing" : status === "unreadable" ? "error" : "done";
  return (
    <Attachment size="sm" state={state} className="max-w-64" title={label}>
      <AttachmentMedia className="text-muted-foreground">
        {status === "reading" ? (
          <Spinner aria-label={t("documentStatus.reading")} />
        ) : status === "unreadable" ? (
          <WarningIcon />
        ) : (
          <Icon />
        )}
      </AttachmentMedia>
      <AttachmentContent>
        <AttachmentTitle>{isLink ? displayLink(label) : label}</AttachmentTitle>
        {status && (
          <AttachmentDescription>
            {t(
              status === "reading"
                ? "documentStatus.reading"
                : status === "read"
                ? "documentStatus.read"
                : "documentStatus.unreadable"
            )}
          </AttachmentDescription>
        )}
      </AttachmentContent>
      {onRemove && (
        <AttachmentActions>
          <AttachmentAction aria-label={removeLabel} onClick={onRemove}>
            <XIcon />
          </AttachmentAction>
        </AttachmentActions>
      )}
    </Attachment>
  );
}

function VaultItemsAttachments({
  items,
  t
}: {
  items: NonNullable<ReturnType<typeof parseVaultItemsMessage>>;
  t: TFunction<"createProject">;
}) {
  const [expanded, setExpanded] = useState(false);
  const hidden = expanded ? 0 : Math.max(0, items.length - ATTACHMENTS_SHOWN);
  return (
    <div className="flex min-w-0 flex-col items-end gap-1.5">
      {(hidden > 0 ? items.slice(0, ATTACHMENTS_SHOWN) : items).map((item, index) => (
        <DocumentStatusAttachment
          key={`${item.kind}-${index}`}
          label={item.label}
          isLink={item.kind === "link"}
          status={item.status}
          t={t}
        />
      ))}
      {hidden > 0 && (
        <Button
          variant="ghost"
          size="sm"
          shape="pill"
          className="text-muted-foreground"
          onClick={() => setExpanded(true)}
        >
          {t("leo.more", { count: hidden })}
        </Button>
      )}
    </div>
  );
}

const LeoCaseBriefMessage = memo(function LeoCaseBriefMessage({
  message,
  isLoading,
  progressMessage,
  continued,
  t
}: {
  message: ChatMessageType;
  isLoading: boolean;
  progressMessage?: string | null;
  // A question right after another of Leo's messages continues that turn: no second avatar.
  continued: boolean;
  t: TFunction<"createProject">;
}) {
  const { isLabs } = useMeStore();

  if (message.role === "user") {
    const vaultItems = parseVaultItemsMessage(message.content);
    return (
      <Message align="end">
        <MessageContent className="items-end gap-2">
          {vaultItems ? (
            <VaultItemsAttachments items={vaultItems} t={t} />
          ) : (
            <LeoUserBubble>
              <p className="whitespace-pre-line">
                {parseNavMarkerMessage(message.content) ??
                  parseDimensionAnswerMessage(message.content) ??
                  message.content}
              </p>
            </LeoUserBubble>
          )}
        </MessageContent>
      </Message>
    );
  }

  if (isLoading) return <LeoThinking steps={[progressMessage || t("leo.thinking")]} />;

  let parsed: { type: string; content: string };
  try {
    parsed = parseMessageContent(message.content, !!isLabs);
  } catch {
    parsed = { type: "error", content: "There was a problem displaying this message." };
  }
  const isError = parsed.type === "error";
  const { lead, question } = isError
    ? { lead: parsed.content, question: null }
    : splitQuestion(parsed.content, templateKey(message));

  return (
    <Message>
      <MessageAvatar className="self-start bg-transparent">
        {continued ? <span className="size-8" aria-hidden="true" /> : <LeoAvatar />}
      </MessageAvatar>
      <MessageContent>
        <Bubble variant="ghost">
          <BubbleContent className="flex flex-col gap-2">
            {isError ? (
              <div className="flex items-start gap-1.5 pt-1 typo-body-sm text-destructive">
                <WarningIcon weight="bold" className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                <div className="min-w-0">
                  <LeoMarkdown content={lead} />
                </div>
              </div>
            ) : (
              lead && (
                <div className="pt-1 typo-body-sm text-foreground">
                  <LeoMarkdown content={lead} />
                </div>
              )
            )}
            {question && (
              <div
                data-slot="leo-question"
                className={cn(
                  "relative flex flex-col items-start gap-1.5 ps-3 typo-body-sm text-foreground before:absolute before:inset-y-0.5 before:start-0 before:w-[1.5px] before:rounded-full before:bg-border",
                  lead || continued ? "mt-1" : "mt-1.5"
                )}
              >
                <LeoMarkdown content={question} />
              </div>
            )}
          </BubbleContent>
        </Bubble>
      </MessageContent>
    </Message>
  );
});

function LaunchReply({
  disabled,
  isLaunching,
  launchingLabel,
  onClick,
  children,
  "data-testid": dataTestId
}: {
  disabled: boolean;
  isLaunching: boolean;
  launchingLabel: string;
  onClick: () => void;
  children: React.ReactNode;
  "data-testid": string;
}) {
  return (
    <Button shape="pill" disabled={disabled} onClick={onClick} data-testid={dataTestId}>
      {isLaunching ? (
        <Spinner data-icon="inline-start" aria-label={launchingLabel} />
      ) : (
        <RocketLaunchIcon data-icon="inline-start" />
      )}
      {children}
    </Button>
  );
}

// The create-project conversation in Leo's look: the same messages and the same step buttons as
// CreateProjectChatMessageList, with the step buttons as Leo's quick replies. `t` speaks the
// conversation's language, like the rest of the chat.
export function CreateProjectLeoConversation({
  messages,
  isLoading,
  loadingMessageId,
  step,
  onLanguageSelect,
  onAddFiles,
  onAddWebLink,
  onSkipFiles,
  onSaveFiles,
  onRemoveFile,
  uploadedDocuments,
  isUploading,
  statusMessage,
  languageOptions = [
    { label: "English", value: "english" },
    { label: "日本語", value: "japanese" }
  ],
  isLanguagesLoading = false,
  labels = {
    upload: "Upload",
    webLink: "Web Link",
    skip: "Skip",
    save: "Save",
    launchAnalysis: "Launch Analysis",
    keepEditing: "Keep Editing",
    dimensionConfirm: "Confirm",
    dimensionSkip: "Skip",
    nextStep: "Next Step"
  },
  onNextStep,
  essentialsDismissed = false,
  onLaunchAnalysis,
  onKeepEditing,
  launchBriefStatus = "incomplete",
  isLaunching = false,
  dimensions = [],
  pendingDimensionIndex = 0,
  selectedDimensionOption = "",
  onDimensionOptionChange,
  onDimensionConfirm,
  onDimensionSkip,
  onSkipVersionName,
  onConfirmDecision,
  onOtherDecision,
  onQuickReply,
  readOnly = false,
  t
}: CreateProjectChatMessageListProps & { t: TFunction<"createProject"> }) {
  // Questions the user answered with "No, change it": the pick and Leo's follow-up show under them.
  const [changeRequests, setChangeRequests] = useState<ReadonlySet<string>>(() => new Set());

  // The greeting stands in for the step flow's opening messages, and setup completion doesn't send the user
  // to another view, so those messages read as the one-page flow does.
  const shown = useMemo(() => {
    const retold: Record<string, string> = {
      uploadFiles: t("leo.welcomePrompt"),
      projectSetupComplete: t("leo.setupComplete")
    };
    return messages
      .filter((message) => templateKey(message) !== "intro")
      .map((message) => {
        const key = templateKey(message);
        const text = key ? retold[key] : undefined;
        return text ? { ...message, content: JSON.stringify({ ...parsedJson(message), content: text }) } : message;
      });
  }, [messages, t]);

  const showWelcome =
    !readOnly &&
    step === "files" &&
    uploadedDocuments.length === 0 &&
    !isUploading &&
    !messages.some((message) => message.role === "user");

  // The conversation follows its end — new messages and text still streaming keep it scrolled down —
  // unless the user has scrolled up to read.
  const end = useRef<HTMLDivElement>(null);
  const pinned = useRef(true);
  useEffect(() => {
    const content = end.current?.parentElement;
    const viewport = end.current?.closest<HTMLElement>("[data-slot=message-scroller-viewport]");
    if (!content || !viewport) return;
    const onScroll = () => {
      pinned.current = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 48;
    };
    const follow = new ResizeObserver(() => {
      if (pinned.current) viewport.scrollTop = viewport.scrollHeight;
    });
    viewport.addEventListener("scroll", onScroll, { passive: true });
    follow.observe(content);
    return () => {
      viewport.removeEventListener("scroll", onScroll);
      follow.disconnect();
    };
  }, [showWelcome]);
  const lastId = shown[shown.length - 1]?.id;
  useEffect(() => {
    const viewport = end.current?.closest<HTMLElement>("[data-slot=message-scroller-viewport]");
    if (!viewport) return;
    pinned.current = true;
    viewport.scrollTop = viewport.scrollHeight;
  }, [lastId, isLoading, showWelcome]);

  const lastAssistantIndex = useMemo(() => {
    for (let i = shown.length - 1; i >= 0; i--) {
      if (shown[i].role === "assistant") return i;
    }
    return -1;
  }, [shown]);

  if (showWelcome) {
    return (
      <LeoWelcome
        title={t("leo.welcomeTitle")}
        description={t("leo.welcomeDescription")}
        prompt={t("leo.welcomePrompt")}
      >
        <LeoWelcomePill icon={FilePlusIcon} onClick={onAddFiles}>
          {t("leo.addFiles")}
        </LeoWelcomePill>
        <LeoWelcomePill icon={GlobeHemisphereWestIcon} onClick={onAddWebLink}>
          {t("leo.addWebLink")}
        </LeoWelcomePill>
        <LeoWelcomePill subtle onClick={onSkipFiles}>
          {labels.skip}
        </LeoWelcomePill>
      </LeoWelcome>
    );
  }

  const renderStepReplies = (afterIndex: number) => {
    if (readOnly || afterIndex !== lastAssistantIndex) return null;
    const lastMessage = shown[afterIndex];
    const key = lastMessage ? templateKey(lastMessage) : null;
    const busy = isLoading;

    if (lastMessage && messageType(lastMessage) === "version_name_prompt" && onSkipVersionName) {
      return (
        <Replies>
          <Reply
            quiet
            disabled={busy}
            onClick={onSkipVersionName}
            data-testid="create-project-chat-skip-version-name-button"
          >
            {labels.skip}
          </Reply>
        </Replies>
      );
    }

    if (lastMessage && messageType(lastMessage) === "essentials_covered" && !essentialsDismissed) {
      return (
        <Replies>
          <LaunchReply
            disabled={launchBriefStatus !== "complete" || isLaunching}
            isLaunching={isLaunching}
            launchingLabel={t("newProject.launching")}
            onClick={() => onLaunchAnalysis?.()}
            data-testid="create-project-chat-launch-analysis-button"
          >
            {labels.launchAnalysis}
          </LaunchReply>
          <Reply
            disabled={busy}
            onClick={() => onKeepEditing?.()}
            data-testid="create-project-chat-keep-editing-button"
          >
            {labels.keepEditing}
          </Reply>
        </Replies>
      );
    }

    if (step === "chat" && WALKTHROUGH_CLOSING_KEYS.has(key ?? "") && onLaunchAnalysis) {
      return (
        <Replies>
          <LaunchReply
            disabled={launchBriefStatus === "empty" || isLaunching}
            isLaunching={isLaunching}
            launchingLabel={t("newProject.launching")}
            onClick={onLaunchAnalysis}
            data-testid="create-project-chat-walkthrough-launch-button"
          >
            {labels.launchAnalysis}
          </LaunchReply>
        </Replies>
      );
    }

    if (key === "backAtProjectSetupNoChanges" && onNextStep) {
      return (
        <Replies>
          <Button shape="pill" disabled={busy} onClick={onNextStep} data-testid="create-project-chat-next-step-button">
            {labels.nextStep}
          </Button>
        </Replies>
      );
    }

    // A walkthrough question already answered (the user's reply follows it) keeps no buttons.
    const answered = afterIndex !== shown.length - 1;
    const confirmableKey = !answered ? key : null;
    const keyQuestionAnswers =
      step === "chat" && confirmableKey === "walkthroughQuestion" && lastMessage ? templateOptions(lastMessage) : null;
    if (
      onConfirmDecision &&
      onOtherDecision &&
      ((step === "decision" && confirmableKey === "decisionFromDocuments") ||
        (step === "chat" && !!confirmableKey && CONFIRMABLE_WALKTHROUGH_KEYS.has(confirmableKey)) ||
        !!keyQuestionAnswers?.length)
    ) {
      const ownAnswers =
        (confirmableKey === "walkthroughCheck" || confirmableKey === "walkthroughReady") && lastMessage
          ? templateOptions(lastMessage) ?? []
          : keyQuestionAnswers;
      if (lastMessage && changeRequests.has(lastMessage.id)) return null;
      return (
        <Replies>
          {ownAnswers ? (
            ownAnswers.map((answer) => (
              <Reply
                key={answer}
                disabled={busy}
                onClick={() => onQuickReply?.(answer)}
                data-testid="create-project-chat-check-answer-button"
              >
                {answer}
              </Reply>
            ))
          ) : (
            <Reply
              disabled={busy}
              onClick={onConfirmDecision}
              data-testid="create-project-chat-confirm-decision-button"
            >
              {labels.confirmDecision}
            </Reply>
          )}
          {!ownAnswers?.length && confirmableKey !== "walkthroughReady" && (
            <Reply
              disabled={busy}
              onClick={() => {
                if (lastMessage) setChangeRequests((current) => new Set(current).add(lastMessage.id));
                onOtherDecision?.();
              }}
              data-testid="create-project-chat-other-decision-button"
            >
              {t("leo.changeIt")}
            </Reply>
          )}
        </Replies>
      );
    }

    if (step === "setupComplete" && onNextStep) {
      return (
        <Replies>
          <Button shape="pill" disabled={busy} onClick={onNextStep} data-testid="create-project-chat-next-step-button">
            {labels.nextStep}
          </Button>
        </Replies>
      );
    }

    if (step === "language") {
      if (isLanguagesLoading) return <Spinner aria-label={t("newProject.loading")} className="ms-1" />;
      return (
        <Replies>
          {languageOptions.map((lang) => (
            <Reply
              key={lang.value}
              onClick={() => onLanguageSelect(lang.value)}
              data-testid={`create-project-chat-language-${lang.value}-button`}
            >
              {lang.label}
            </Reply>
          ))}
        </Replies>
      );
    }

    if (step === "files") {
      const hasItems = uploadedDocuments.length > 0;
      return (
        <div className="flex flex-col gap-3">
          {hasItems && (
            <div className="flex min-w-0 flex-col gap-1.5">
              {uploadedDocuments.map((doc) => {
                const label = doc.webUrl ?? removeTimestampPrefix(doc.fileName);
                return (
                  <DocumentStatusAttachment
                    key={doc.documentId}
                    label={label}
                    isLink={!!doc.webUrl || isWebUrl(label)}
                    removeLabel={t("leo.remove", { name: label })}
                    onRemove={() => onRemoveFile(doc.documentId)}
                    t={t}
                  />
                );
              })}
            </div>
          )}
          {isUploading && (
            <span className="flex items-center gap-2 typo-body-sm text-muted-foreground">
              <Spinner aria-label={t("leo.uploading")} />
              {t("leo.uploading")}
            </span>
          )}
          <Replies>
            <Reply disabled={isUploading} onClick={onAddFiles} data-testid="create-project-chat-upload-files-button">
              <FilePlusIcon data-icon="inline-start" />
              {t("leo.addFiles")}
            </Reply>
            <Reply onClick={onAddWebLink} data-testid="create-project-chat-add-web-link-button">
              <GlobeHemisphereWestIcon data-icon="inline-start" />
              {t("leo.addWebLink")}
            </Reply>
            {hasItems ? (
              <Button
                shape="pill"
                disabled={isUploading}
                onClick={onSaveFiles}
                data-testid="create-project-chat-save-files-button"
              >
                {labels.save}
              </Button>
            ) : (
              <Reply quiet onClick={onSkipFiles} data-testid="create-project-chat-skip-files-button">
                {labels.skip}
              </Reply>
            )}
          </Replies>
        </div>
      );
    }

    if (step === "dimensions") {
      const dimension = dimensions[pendingDimensionIndex];
      if (!dimension) return null;
      if (dimension.type !== "dropdown") {
        return (
          <Replies>
            <Reply quiet onClick={() => onDimensionSkip?.()} data-testid="create-project-chat-dimension-skip-button">
              {labels.dimensionSkip}
            </Reply>
          </Replies>
        );
      }
      return (
        <div className="flex flex-col gap-2">
          <Replies>
            {(dimension.options ?? []).map((option) => (
              <Reply
                key={option}
                aria-pressed={selectedDimensionOption === option}
                className="aria-pressed:border-primary aria-pressed:bg-primary/10"
                onClick={() => onDimensionOptionChange?.(option)}
                data-testid={`create-project-chat-dimension-option-${option}-button`}
              >
                {option}
              </Reply>
            ))}
          </Replies>
          <Replies>
            <Button
              shape="pill"
              disabled={!selectedDimensionOption}
              onClick={() => onDimensionConfirm?.(selectedDimensionOption)}
              data-testid="create-project-chat-dimension-confirm-button"
            >
              {labels.dimensionConfirm}
            </Button>
            <Reply quiet onClick={() => onDimensionSkip?.()} data-testid="create-project-chat-dimension-skip-button">
              {labels.dimensionSkip}
            </Reply>
          </Replies>
        </div>
      );
    }

    return null;
  };

  return (
    <MessageScrollerProvider autoScroll>
      <MessageScroller>
        <MessageScrollerViewport
          aria-label={t("leo.conversation")}
          className="no-scrollbar [scrollbar-gutter:auto]! [scrollbar-width:none]!"
        >
          <MessageScrollerContent className="gap-6 py-4">
            {shown.map((message, index) => {
              const loading =
                message.id === loadingMessageId ||
                (isLoading && !loadingMessageId && index === shown.length - 1 && message.role === "assistant");
              const replies = message.role === "assistant" ? renderStepReplies(index) : null;
              const previous = shown[index - 1];
              const continued =
                !loading &&
                message.role === "assistant" &&
                previous?.role === "assistant" &&
                QUESTION_KEYS.has(templateKey(message) ?? "");
              return (
                <Fragment key={message.id}>
                  <MessageScrollerItem messageId={message.id} className={cn(continued && "-mt-4")}>
                    <div className="flex flex-col gap-2.5">
                      <LeoCaseBriefMessage
                        message={message}
                        isLoading={loading}
                        progressMessage={
                          isLoading && index === shown.length - 1 && message.role === "assistant"
                            ? statusMessage
                            : undefined
                        }
                        continued={continued}
                        t={t}
                      />
                      {replies && <div className="ps-10 pt-1">{replies}</div>}
                    </div>
                  </MessageScrollerItem>
                  {changeRequests.has(message.id) && (
                    <>
                      <MessageScrollerItem messageId={`${message.id}-change`}>
                        <Message align="end">
                          <MessageContent className="items-end gap-2">
                            <LeoUserBubble>
                              <p className="whitespace-pre-line">{t("leo.changeItReply")}</p>
                            </LeoUserBubble>
                          </MessageContent>
                        </Message>
                      </MessageScrollerItem>
                      <MessageScrollerItem messageId={`${message.id}-change-prompt`}>
                        <Message>
                          <MessageAvatar className="self-start bg-transparent">
                            <LeoAvatar />
                          </MessageAvatar>
                          <MessageContent>
                            <Bubble variant="ghost">
                              <BubbleContent className="flex flex-col gap-2">
                                <div
                                  data-slot="leo-question"
                                  className="relative mt-1.5 flex flex-col items-start gap-1.5 ps-3 typo-body-sm text-foreground before:absolute before:inset-y-0.5 before:start-0 before:w-[1.5px] before:rounded-full before:bg-border"
                                >
                                  <p>{t("leo.changePrompt")}</p>
                                </div>
                              </BubbleContent>
                            </Bubble>
                          </MessageContent>
                        </Message>
                      </MessageScrollerItem>
                    </>
                  )}
                </Fragment>
              );
            })}
            {isLoading && shown[shown.length - 1]?.role !== "assistant" && (
              <MessageScrollerItem messageId="leo-thinking">
                <LeoThinking steps={[statusMessage || t("leo.thinking")]} />
              </MessageScrollerItem>
            )}
            <div ref={end} aria-hidden="true" />
          </MessageScrollerContent>
        </MessageScrollerViewport>
        <MessageScrollerButton label={t("leo.scrollToEnd")} />
      </MessageScroller>
    </MessageScrollerProvider>
  );
}
